import React, { useState, useEffect } from "react";
import { useHistory } from "react-router";
import { DataService } from "../../Services/dataService";
import Loader from "../../Components/Loader";
import { SingleRequestPanel, collectRateRequests, quoteSummary } from "./RateRequestProfile";

const STATUS_STYLES = {
  requested: { bg: "#fff3cd", color: "#856404", border: "#ffc107", label: "Requested" },
  pending: { bg: "#f8d7da", color: "#842029", border: "#f5c2c7", label: "Not requested" },
  rejected: { bg: "#fde8e8", color: "#9b1c1c", border: "#f8b4b4", label: "Needs update" },
  approved: { bg: "#d1e7dd", color: "#0f5132", border: "#badbcc", label: "Approved" },
};

const TABS = [
  { id: "requested", label: "Requested" },
  { id: "rejected", label: "Needs update" },
  { id: "pending", label: "Not requested" },
  { id: "approved", label: "Approved" },
];

const formatDate = (dateStr) => {
  if (!dateStr) return "-";
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return "-";
  return d.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const StatusBadge = ({ status }) => {
  const s = STATUS_STYLES[status] || STATUS_STYLES.pending;
  return (
    <span
      style={{
        background: s.bg,
        color: s.color,
        border: `1px solid ${s.border}`,
        padding: "3px 10px",
        borderRadius: "12px",
        fontSize: "12px",
        fontWeight: "600",
        whiteSpace: "nowrap",
      }}
    >
      {s.label}
    </span>
  );
};

const requestTime = (req) => (req && (req.submitted_at || req.requested_at)) || null;

const RetailerRequestsTable = ({ requests }) => {
  const [openRequestId, setOpenRequestId] = useState(null);

  if (!requests.length) {
    return (
      <div style={{ textAlign: "center", padding: "20px", color: "#6c757d" }}>
        This retailer has not sent any rate requests yet.
      </div>
    );
  }

  return (
    <table className="table table-sm" style={{ marginBottom: 0, background: "#fff" }}>
      <thead style={{ background: "#eef2f7" }}>
        <tr>
          <th style={{ width: "40px" }}>#</th>
          <th>Quote</th>
          <th>Lane</th>
          <th>Service</th>
          <th>Total payable</th>
          <th>Status</th>
          <th>Requested</th>
          <th style={{ width: "90px" }}></th>
        </tr>
      </thead>
      <tbody>
        {requests.map((req, idx) => {
          const key = req.request_id || `req_${idx}`;
          const summary = quoteSummary(req);
          const isOpen = openRequestId === key;
          return (
            <React.Fragment key={key}>
              <tr>
                <td>{requests.length - idx}</td>
                <td style={{ fontWeight: 600 }}>{req.quote_token || "-"}</td>
                <td style={{ fontWeight: 600 }}>{summary.lane}</td>
                <td>{summary.courier}</td>
                <td style={{ fontWeight: 700, color: "#28a745" }}>{summary.total}</td>
                <td>
                  <StatusBadge status={req.status || "requested"} />
                </td>
                <td>{formatDate(requestTime(req))}</td>
                <td>
                  <button
                    type="button"
                    onClick={() => setOpenRequestId(isOpen ? null : key)}
                    style={{
                      background: "transparent",
                      color: "#0323fe",
                      border: "1px solid #0323fe",
                      borderRadius: "6px",
                      padding: "3px 10px",
                      fontSize: "12px",
                      fontWeight: 600,
                      cursor: "pointer",
                    }}
                  >
                    {isOpen ? "Hide" : "Details"}
                  </button>
                </td>
              </tr>
              {isOpen && (
                <tr>
                  <td colSpan="8" style={{ background: "#f8fafc" }}>
                    <SingleRequestPanel data={req} index={idx} totalCount={requests.length} />
                  </td>
                </tr>
              )}
            </React.Fragment>
          );
        })}
      </tbody>
    </table>
  );
};

export const RateApprovalList = () => {
  const history = useHistory();
  const [listData, setListData] = useState([]);
  const [loader, setLoader] = useState(true);
  const [total, setTotal] = useState(0);
  const [expandedRetailer, setExpandedRetailer] = useState(null);
  const [tab, setTab] = useState("requested");
  const [actionRetailer, setActionRetailer] = useState(null);
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchList();
  }, [tab]);

  const fetchList = () => {
    setLoader(true);
    DataService.get("/admin/get_rate_approval_requests", { tab, limit: 100, flatten: 0 })
      .then((res) => {
        if (res && res.status) {
          setListData(res.data || []);
          setTotal((res.pagination && res.pagination.total) || 0);
        }
        setLoader(false);
      })
      .catch(() => setLoader(false));
  };

  const openAction = (retailer, mode) => {
    setActionRetailer({ retailer, mode });
    setMessage("");
  };

  const closeAction = () => {
    setActionRetailer(null);
    setMessage("");
    setSubmitting(false);
  };

  const submitAction = () => {
    if (!actionRetailer) return;
    if (actionRetailer.mode === "reject" && !message.trim()) {
      alert("Please enter a message for the retailer.");
      return;
    }

    setSubmitting(true);
    const endpoint =
      actionRetailer.mode === "approve"
        ? "/admin/approve_retailer_rate"
        : "/admin/reject_retailer_rate";

    DataService.post(endpoint, {
      retailer_id: actionRetailer.retailer.retailer_id || actionRetailer.retailer._id,
      message: message.trim(),
    })
      .then((res) => {
        setSubmitting(false);
        if (res && res.status) {
          alert(res.message || "Done");
          closeAction();
          fetchList();
        } else {
          alert((res && res.message) || "Action failed");
        }
      })
      .catch(() => {
        setSubmitting(false);
        alert("An error occurred");
      });
  };

  const actionOpenCount = actionRetailer
    ? actionRetailer.retailer.open_requests || 0
    : 0;

  return (
    <div className="row">
      <div className="col-lg-12">
        <div className="card">
          <div className="card-body">
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: "20px",
                flexWrap: "wrap",
                gap: "12px",
              }}
            >
              <div>
                <h4 className="card-title" style={{ marginBottom: "4px" }}>
                  Rate Approval Requests
                </h4>
                <p style={{ color: "#6c757d", fontSize: "13px", margin: 0 }}>
                  One row per retailer. Expand a retailer to see every rate request they sent.
                  Approving a retailer approves all of their open requests and notifies them by email
                  and in their dashboard.
                </p>
              </div>
              <div
                style={{
                  background: "#fff3cd",
                  border: "1px solid #ffc107",
                  borderRadius: "8px",
                  padding: "8px 16px",
                  fontSize: "13px",
                  fontWeight: "600",
                  color: "#856404",
                }}
              >
                {total} retailer{total !== 1 ? "s" : ""}
              </div>
            </div>

            <div style={{ display: "flex", gap: "8px", marginBottom: "16px", flexWrap: "wrap" }}>
              {TABS.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setTab(t.id)}
                  style={{
                    border: tab === t.id ? "1px solid #038559" : "1px solid #dee2e6",
                    background: tab === t.id ? "#038559" : "#fff",
                    color: tab === t.id ? "#fff" : "#495057",
                    borderRadius: "8px",
                    padding: "8px 14px",
                    fontSize: "12px",
                    fontWeight: "700",
                    cursor: "pointer",
                  }}
                >
                  {t.label}
                </button>
              ))}
            </div>

            {loader ? (
              <div style={{ textAlign: "center", padding: "40px" }}>
                <Loader />
              </div>
            ) : listData.length === 0 ? (
              <div style={{ textAlign: "center", padding: "60px 20px", color: "#6c757d" }}>
                <h5>No records in this tab</h5>
                <p>Switch tabs to review other retailers.</p>
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table table-hover" style={{ borderCollapse: "collapse" }}>
                  <thead style={{ background: "#f8f9fa" }}>
                    <tr>
                      <th style={{ width: "40px" }}></th>
                      <th>#</th>
                      <th>Retailer</th>
                      <th>Requests</th>
                      <th>All requests</th>
                      <th>Status</th>
                      <th>Last requested</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {listData.map((item, idx) => {
                      const retailerId = String(item.retailer_id || item._id);
                      const requests = collectRateRequests(item);
                      const latest = requests[0] || null;
                      const latestSummary = quoteSummary(latest);
                      const openCount = item.open_requests || 0;
                      const isExpanded = expandedRetailer === retailerId;
                      const status = item.rate_approval_status;
                      return (
                        <React.Fragment key={retailerId}>
                          <tr>
                            <td
                              onClick={() => setExpandedRetailer(isExpanded ? null : retailerId)}
                              style={{
                                textAlign: "center",
                                color: "#0323fe",
                                fontWeight: "bold",
                                cursor: "pointer",
                              }}
                            >
                              {isExpanded ? "▲" : "▼"}
                            </td>
                            <td>{idx + 1}</td>
                            <td>
                              <strong>{item.retailer_name || "(No name)"}</strong>
                              <br />
                              <small style={{ color: "#6c757d" }}>{item.retailer_email}</small>
                              <br />
                              <small style={{ color: "#6c757d" }}>{item.phone_number || "-"}</small>
                            </td>
                            <td>
                              <button
                                type="button"
                                onClick={() => setExpandedRetailer(isExpanded ? null : retailerId)}
                                style={{
                                  background: "#e7f1ff",
                                  color: "#084298",
                                  border: "none",
                                  borderRadius: "12px",
                                  padding: "3px 10px",
                                  fontSize: "12px",
                                  fontWeight: 700,
                                  cursor: "pointer",
                                }}
                              >
                                {requests.length} request{requests.length !== 1 ? "s" : ""}
                              </button>
                              {openCount > 0 && (
                                <div style={{ fontSize: "11px", color: "#856404", fontWeight: 700, marginTop: "4px" }}>
                                  {openCount} awaiting review
                                </div>
                              )}
                            </td>
                            <td>
                              {requests.length > 0 ? (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                  {requests.map((r, i) => {
                                    const s = quoteSummary(r);
                                    return (
                                      <div key={i} style={{ borderBottom: i < requests.length - 1 ? '1px dashed #dee2e6' : 'none', paddingBottom: i < requests.length - 1 ? '4px' : '0' }}>
                                        <div style={{ fontSize: "13px", fontWeight: "600" }}>{s.lane}</div>
                                        <small style={{ color: "#6c757d" }}>
                                          {s.courier} · {s.total}
                                        </small>
                                      </div>
                                    );
                                  })}
                                </div>
                              ) : (
                                <small style={{ color: "#6c757d" }}>-</small>
                              )}
                            </td>
                            <td>
                              <StatusBadge status={status} />
                            </td>
                            <td>{formatDate(item.latest_request_at || item.updated_at)}</td>
                            <td>
                              <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                                {(status === "requested" || status === "rejected" || openCount > 0) && (
                                  <button
                                    type="button"
                                    onClick={() => openAction(item, "approve")}
                                    style={{
                                      background: "linear-gradient(135deg, #28a745, #20c997)",
                                      color: "white",
                                      border: "none",
                                      borderRadius: "6px",
                                      padding: "6px 14px",
                                      fontSize: "12px",
                                      cursor: "pointer",
                                      fontWeight: "600",
                                    }}
                                  >
                                    {openCount > 1 ? `Approve all (${openCount})` : "Approve"}
                                  </button>
                                )}
                                {status !== "approved" && (
                                  <button
                                    type="button"
                                    onClick={() => openAction(item, "reject")}
                                    style={{
                                      background: "#fff",
                                      color: "#dc3545",
                                      border: "1px solid #dc3545",
                                      borderRadius: "6px",
                                      padding: "6px 12px",
                                      fontSize: "12px",
                                      cursor: "pointer",
                                      fontWeight: "600",
                                    }}
                                  >
                                    Message / Reject
                                  </button>
                                )}
                                <button
                                  type="button"
                                  onClick={() => history.push("/retailer-details?id=" + retailerId)}
                                  style={{
                                    background: "transparent",
                                    color: "#0323fe",
                                    border: "1px solid #0323fe",
                                    borderRadius: "6px",
                                    padding: "6px 12px",
                                    fontSize: "12px",
                                    cursor: "pointer",
                                    fontWeight: "600",
                                  }}
                                >
                                  Profile
                                </button>
                              </div>
                            </td>
                          </tr>

                          {isExpanded && (
                            <tr>
                              <td colSpan="8" style={{ padding: "12px 16px", background: "#f8fafc" }}>
                                {(item.rate_admin_message || item.rate_rejected_reason) && (
                                  <div
                                    style={{
                                      padding: "8px 12px",
                                      background: "#fff8e6",
                                      border: "1px solid #ffe08a",
                                      borderRadius: "8px",
                                      marginBottom: "10px",
                                      fontSize: "13px",
                                    }}
                                  >
                                    <strong>Last admin message:</strong>{" "}
                                    {item.rate_admin_message || item.rate_rejected_reason}
                                  </div>
                                )}
                                <RetailerRequestsTable requests={requests} />
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      {actionRetailer && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(15,23,42,0.45)",
            zIndex: 2000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "20px",
          }}
        >
          <div
            style={{
              width: "100%",
              maxWidth: "480px",
              background: "#fff",
              borderRadius: "12px",
              padding: "24px",
              boxShadow: "0 20px 50px rgba(0,0,0,0.2)",
            }}
          >
            <h5 style={{ marginBottom: "8px" }}>
              {actionRetailer.mode === "approve"
                ? `Approve ${actionRetailer.retailer.retailer_name || "retailer"}`
                : "Send message / request update"}
            </h5>
            <p style={{ color: "#6c757d", fontSize: "13px", marginBottom: "16px" }}>
              {actionRetailer.mode === "approve"
                ? `This approves ${
                    actionOpenCount > 0
                      ? `all ${actionOpenCount} open request${actionOpenCount !== 1 ? "s" : ""}`
                      : "the retailer's rate setup"
                  }. The retailer gets an email and a dashboard notification, and can book the approved quotes directly from My Quotations. Optional note below is included in both.`
                : "Required message is emailed and shown as a dashboard notification. Booking stays blocked until you approve."}
            </p>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={4}
              placeholder={
                actionRetailer.mode === "approve"
                  ? "Optional note for retailer..."
                  : "Explain what needs to be corrected..."
              }
              style={{
                width: "100%",
                border: "1px solid #ced4da",
                borderRadius: "8px",
                padding: "12px",
                fontSize: "14px",
                resize: "vertical",
              }}
            />
            <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px", marginTop: "16px" }}>
              <button
                type="button"
                onClick={closeAction}
                disabled={submitting}
                style={{
                  border: "1px solid #ced4da",
                  background: "#fff",
                  borderRadius: "8px",
                  padding: "8px 14px",
                  fontWeight: "600",
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={submitAction}
                disabled={submitting}
                style={{
                  border: "none",
                  background: actionRetailer.mode === "approve" ? "#28a745" : "#dc3545",
                  color: "#fff",
                  borderRadius: "8px",
                  padding: "8px 16px",
                  fontWeight: "700",
                }}
              >
                {submitting
                  ? "Saving..."
                  : actionRetailer.mode === "approve"
                    ? "Confirm Approve"
                    : "Send Feedback"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
